import { ChatPane } from './ChatPane';
import { useUiStore, type Context } from '@/lib/store/ui';
import { useDronesStore } from '@/lib/store/drones';
import { useAlertsStore } from '@/lib/store/alerts';

const SCOPE_LABEL: Record<Context, string> = {
  drones: 'Active Drones',
  oi: 'Active OI',
  flights: 'Flights',
  surv: 'Surveillance',
  infra: 'Infrastructure',
  rtm: 'RTM Services',
  inv: 'Inventory',
  ds: 'Data Stream',
  apps: 'Apps',
};

export function ServicesIntelligence() {
  const ctx = useUiStore((s) => s.activeContext);

  return (
    <ChatPane
      title="Services Intelligence"
      scopeLabel={SCOPE_LABEL[ctx]}
      suggestions={[
        "What's the closest aircraft right now?",
        'Why is DAA degraded?',
        'Any critical alerts?',
      ]}
      initialMessage="I have visibility into live drone telemetry, surveillance health, OIs, and RTM service metrics. Ask me anything in scope."
      send={async (q, _hist, onToken) => streamMock(q, onToken)}
    />
  );
}

function buildAnswer(q: string): string {
  const drones = Object.values(useDronesStore.getState().byId);
  const alerts = useAlertsStore.getState().items;
  const crit = alerts.filter((a) => a.severity === 'crit' && !a.acknowledged);
  const ql = q.toLowerCase();

  if (ql.includes('closest')) {
    return drones.length === 0
      ? 'No drones in view right now.'
      : `Currently tracking ${drones.length} drones; closest by alert priority is ${crit[0]?.drone_id ?? drones[0]?.telemetry.callsign ?? 'unknown'}.`;
  }
  if (ql.includes('daa')) {
    return `DAA is degraded for any drone where 1+ surveillance services have offline sensors. Right now: ${
      drones
        .filter((d) => d.telemetry.status === 'DAA-DEG' || d.telemetry.status === 'SA')
        .map((d) => d.telemetry.callsign)
        .join(', ') || 'none'
    }.`;
  }
  if (ql.includes('alert') || ql.includes('critical')) {
    return crit.length === 0
      ? 'No unacknowledged critical alerts.'
      : `${crit.length} unacknowledged critical alert(s):\n${crit
          .slice(0, 5)
          .map((a) => `• ${a.message}`)
          .join('\n')}`;
  }
  return `I'd consult MQTT topics (\`telemetry\`, \`events\`, \`surveillance\`) and REST endpoints (\`/api/oi\`, and the DSS OI search via \`/oi-api\`) to answer "${q}". In production this routes through a backend proxy.`;
}

async function streamMock(q: string, onToken: (t: string) => void) {
  const answer = buildAnswer(q);
  for (const word of answer.split(/(\s+)/)) {
    await new Promise((r) => setTimeout(r, 28));
    onToken(word);
  }
}
